import { type ForManagerLocationDetail, getMarketsListQueryOptions } from "@sidegigllc/api/client";
import type { GigsQueryPayload } from "api/queries/stafferGigs";
import { queryListGigsOptions } from "api/queries/stafferGigs";
import type { GigView } from "domains/gigs/containers/ViewSwitcher/gigView";
import { interviewFeedQueries, loadedGigsQueries } from "domains/schedule/containers/Calendar/queries";
import { listFeedRange } from "domains/schedule/utils/interviewFeed";
import { getScheduleParams, withWorkingInterviews } from "domains/schedule/utils/scheduleParams";
import type { MarketplaceSearch } from "domains/schedule/utils/useMarketplaceSearch";
import type { WarmableQuery } from "routing/warmQueries";
import type { ViewType } from ".";

export type GigBoardArgs = Partial<GigsQueryPayload> & { viewType: ViewType };

export type GigsQueryInput = {
  search: MarketplaceSearch;
  view: GigView;
  now: Date;
  base?: (now: Date) => Partial<GigsQueryPayload>;
  overrides?: (now: Date) => Partial<GigsQueryPayload>;
  /** Also request working-interview shifts (`jobs-in-main-menu` on, Jobs access), as the calendar does. */
  includeWorkingInterviews?: boolean;
  /** Also request task shifts (`qwick-tasks` on). */
  includeTasks?: boolean;
};

export const gigsQuery = ({
  search,
  view,
  now,
  base,
  overrides,
  includeWorkingInterviews,
  includeTasks,
}: GigsQueryInput) => {
  const { params, enabled } = getScheduleParams(search, view, { includeTasks });
  const baseArgs = base?.(now) ?? {};
  return queryListGigsOptions(
    {
      managed: false,
      ...baseArgs,
      ...params,
      types: withWorkingInterviews(params.types, includeWorkingInterviews),
      ordering: params.ordering ?? baseArgs.ordering ?? "start_time",
      ...overrides?.(now),
    },
    { enabled },
  );
};

export const marketsQuery = ({ isAdmin }: { isAdmin: boolean }) =>
  getMarketsListQueryOptions(undefined, { query: { enabled: isAdmin, staleTime: Number.POSITIVE_INFINITY } });

type QueriesInput = {
  search: MarketplaceSearch;
  location?: ForManagerLocationDetail;
  now: Date;
  isAdmin: boolean;
  queryArgs: (now: Date) => GigBoardArgs;
  includeWorkingInterviews?: boolean;
  includeTasks?: boolean;
};

/** What the list asks of the hiring-schedule feed: its chunks from today, and the gigs they are deduped against. */
export const interviewQueries = ({ search, location, now, includeWorkingInterviews, includeTasks }: QueriesInput) => {
  const input = { search, location, includeWorkingInterviews, includeTasks, range: listFeedRange(now) };
  return [...interviewFeedQueries(input), ...loadedGigsQueries(input)];
};

export const queries = (input: QueriesInput): Array<WarmableQuery> => [
  gigsQuery({
    search: input.search,
    view: "list",
    now: input.now,
    base: input.queryArgs,
    includeWorkingInterviews: input.includeWorkingInterviews,
    includeTasks: input.includeTasks,
  }),
  marketsQuery({ isAdmin: input.isAdmin }),
  ...(input.includeWorkingInterviews ? interviewQueries(input) : []),
];
